import React, { useEffect, useState } from 'react';
import { Backdrop, Box, Button, IconButton, Modal, Fade, Tooltip, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import Description from '@mui/icons-material/Description';
import LoadingButton from "@mui/lab/LoadingButton";
import { withSnackbar } from '../../../utils/SnackbarHOC';
import { checkFileExists, getCvBlob } from '../../../services/FileService';

const paperStyle = {
    backgroundColor: '#fff',
    boxShadow: '0 24px 80px rgba(15,23,42,0.32)',
    borderRadius: '12px',
    // Large on desktop, almost full width on phones (was min 900px).
    width: 'min(96vw, max(82vw, 900px))',
    maxWidth: '1320px',
    height: '94vh',
    zIndex: 100000,
    outline: 'none',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
};

function ShowAddCvCandidat({ children, snackbarShowMessage, files, fileName, ...otherProps }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [iframe, setIframe] = useState('');
    const searchCv = files?.filter((cv) => cv.categorie === 'cv');
    const cvFile = searchCv?.[0];
    const cvKey = cvFile?.id ?? cvFile?.image_name ?? cvFile?.imageName;
    const [fileExists, setFileExists] = useState(true);

    useEffect(() => {
        let isActive = true;
        setFileExists(true);
        if (cvFile?.id) {
            checkFileExists(cvFile.id).then((exists) => {
                if (isActive) setFileExists(exists);
            });
        }
        return () => {
            isActive = false;
        };
    }, [cvFile?.id]);

    const handleShowButton = () => (
        <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
            {searchCv?.length > 0 ? (
                <Tooltip title={fileExists ? 'Ouvrir CV.' : "Fichier introuvable : le CV n'existe plus sur le serveur."}>
                    <span>
                        <LoadingButton
                            sx={{ color: '#03407b' }}
                            onClick={handleOpen}
                            disabled={!fileExists}
                            variant="text"
                            size="medium"
                            loading={loading}
                            loadingPosition="start"
                            startIcon={<Description />}
                        >
                            <span>{children ?? ''}</span>
                        </LoadingButton>
                    </span>
                </Tooltip>
            ) : (
                <span>{children ?? 'CV'}</span>
            )}
        </Box>
    );

    const getIframe = async (cv) => {
        const res = await getCvBlob(cv);
        const fileUrl = window.URL.createObjectURL(res.data);
        return (
            <div
                style={{ flex: 1, minHeight: 0, height: '100%' }}
                dangerouslySetInnerHTML={{
                    __html: `<iframe title="afficher cv" src="${fileUrl}#view=FitH" width="100%" height="100%" style="border:0;background:#252525;" />`,
                }}
            />
        );
    };

    const handleDownload = async () => {
        try {
            const res = await getCvBlob(cvKey);
            const fileUrl = window.URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            snackbarShowMessage('Impossible de télécharger le CV, fichier physique introuvable', 'error', 4000);
        }
    };

    const handleOpen = async () => {
        try {
            setLoading(true);
            const iframe = await getIframe(cvKey);
            setIframe(iframe);
            setOpen(true);
        } catch (e) {
            setFileExists(false);
            snackbarShowMessage('Impossible de charger le CV, fichier physique introuvable', 'error', 4000);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => setOpen(false);

    const body = (
        <div style={paperStyle}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 44, padding: '0 14px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', fontFamily: "'Fira Sans', sans-serif" }}>
                    CV CANDIDAT
                </Typography>
                <IconButton
                    aria-label="Fermer le CV"
                    onClick={handleClose}
                    size="small"
                    sx={{ color: '#475569', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', '&:hover': { background: '#e2e8f0' } }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', background: '#242424', padding: '18px 22px', overflow: 'hidden' }}>
                {iframe}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px', background: '#f8fafc', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                <Button
                    onClick={handleDownload}
                    sx={{ background: 'linear-gradient(135deg, #0556a4, #03407b)', color: '#fff', borderRadius: '10px', textTransform: 'none', fontWeight: 600, boxShadow: '0 2px 8px rgba(3,64,123,0.28)', '&:hover': { background: 'linear-gradient(135deg, #03407b, #022d68)' } }}
                >
                    Télécharger le CV
                </Button>
            </div>
        </div>
    );

    return (
        <>
            {handleShowButton()}
            <Modal
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1, md: 2 } }}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500, sx: { backgroundColor: 'rgba(15,23,42,0.62)', backdropFilter: 'blur(3px)' } }}
                open={open}
                onClose={handleClose}
            >
                <Fade in={open}>{body}</Fade>
            </Modal>
        </>
    );
}

export default withSnackbar(ShowAddCvCandidat);
